import '../css/training.css';
import EnrollForm from './EnrollForm';
import { COURSE_DETAILS, HIGHLIGHTS, JOURNEY, SCHOOL_NAME } from '../trainingInfo';

const icon = {
  stroke: 'currentColor',
  fill: 'none',
  strokeWidth: 1.6,
  strokeLinecap: 'round',
  strokeLinejoin: 'round',
};

const HIGHLIGHT_ICONS = [
  // hands-on
  <svg viewBox='0 0 24 24' {...icon}><path d='M8 13V5.5a1.5 1.5 0 0 1 3 0V12' /><path d='M11 11.5v-7a1.5 1.5 0 0 1 3 0V12' /><path d='M14 11.5V6.5a1.5 1.5 0 0 1 3 0v8a6 6 0 0 1-6 6h-.5a6 6 0 0 1-4.6-2.2L3.3 15a1.6 1.6 0 0 1 2.4-2.1L8 15' /></svg>,
  // clipboard check
  <svg viewBox='0 0 24 24' {...icon}><rect x='5' y='4' width='14' height='17' rx='2' /><path d='M9 4V3h6v1' /><path d='m9 13 2 2 4-4' /></svg>,
  // heart
  <svg viewBox='0 0 24 24' {...icon}><path d='M12 20s-7-4.4-7-10a4 4 0 0 1 7-2.6A4 4 0 0 1 19 10c0 5.6-7 10-7 10Z' /><path d='M9.5 11h5M12 8.5v5' /></svg>,
  // team
  <svg viewBox='0 0 24 24' {...icon}><circle cx='9' cy='8' r='3' /><circle cx='17' cy='9' r='2.4' /><path d='M3.5 19a5.5 5.5 0 0 1 11 0' /><path d='M15 14.2a4.5 4.5 0 0 1 5.5 4.8' /></svg>,
];

const WORKPLACES = [
  { name: 'Hospitals', svg: <svg viewBox='0 0 24 24' {...icon}><path d='M4 21V7l8-4 8 4v14' /><path d='M12 8v6M9 11h6' /><path d='M10 21v-3h4v3' /></svg> },
  { name: 'Doctors’ offices', svg: <svg viewBox='0 0 24 24' {...icon}><path d='M6 3v6a4 4 0 0 0 8 0V3' /><path d='M10 13v2a5 5 0 0 0 10 0v-2' /><circle cx='20' cy='11' r='2' /></svg> },
  { name: 'Nursing homes', svg: <svg viewBox='0 0 24 24' {...icon}><path d='M3 11 12 4l9 7' /><path d='M5 10v11h14V10' /><path d='M12 18s-3-1.8-3-4a1.6 1.6 0 0 1 3-.8 1.6 1.6 0 0 1 3 .8c0 2.2-3 4-3 4Z' /></svg> },
];

function Training() {
  const details = COURSE_DETAILS.filter((d) => d.value);

  return (
    <div className='training'>
      {/* Hero */}
      <section className='tr_hero' id='training'>
        <div className='tr_inner tr_hero_grid'>
          <div className='tr_hero_copy'>
            <p className='tr_eyebrow'>{SCHOOL_NAME} &middot; Professional Training</p>
            <h1 className='tr_title'>
              Begin a rewarding career in <em>nursing care.</em>
            </h1>
            <p className='tr_lede'>
              Hands-on CNA training and state board preparation, taught by a practicing nurse
              with a master&rsquo;s degree in nursing.
            </p>
            <div className='tr_ctas'>
              <a className='btn btn_primary' href='#enroll'>Request information</a>
              <a className='btn btn_ghost' href='#what-cnas-do'>What does a CNA do?</a>
            </div>
          </div>

          <ol className='tr_journey' aria-label='Your path to certification'>
            <li className='tr_journey_head'>A great journey towards a healthcare career</li>
            {JOURNEY.map((j, i) => (
              <li key={j.step} className='tr_journey_step' style={{ animationDelay: `${0.35 + i * 0.12}s` }}>
                <span className='tr_journey_num'>{i + 1}</span>
                <div>
                  <strong>{j.step}</strong>
                  <span>{j.text}</span>
                </div>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* Highlights */}
      <section className='tr_section tr_cream'>
        <div className='tr_inner'>
          <h2 className='tr_h2'>Why train with us</h2>
          <div className='tr_cards'>
            {HIGHLIGHTS.map((h, i) => (
              <article className='tr_card' key={h.title}>
                <div className='tr_card_icon' aria-hidden='true'>{HIGHLIGHT_ICONS[i % HIGHLIGHT_ICONS.length]}</div>
                <h3>{h.title}</h3>
                <p>{h.text}</p>
              </article>
            ))}
          </div>

          {details.length > 0 && (
            <dl className='tr_details'>
              {details.map((d) => (
                <div key={d.label}>
                  <dt>{d.label}</dt>
                  <dd>{d.value}</dd>
                </div>
              ))}
            </dl>
          )}
        </div>
      </section>

      {/* What a CNA does */}
      <section className='tr_section tr_white' id='what-cnas-do'>
        <div className='tr_inner tr_split'>
          <div>
            <h2 className='tr_h2'>What does a CNA do?</h2>
            <p className='tr_body'>
              A Certified Nursing Assistant works alongside nurses and doctors, helping patients and
              residents with their daily care: bathing, dressing, eating, moving safely, and checking
              vital signs. CNAs are often the people patients see most, which makes the work
              personal and deeply rewarding.
            </p>
          </div>
          <ul className='tr_places'>
            {WORKPLACES.map((w) => (
              <li key={w.name}>
                <span className='tr_place_icon' aria-hidden='true'>{w.svg}</span>
                {w.name}
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* Instructor */}
      <section className='tr_section tr_cream'>
        <div className='tr_inner tr_instructor'>
          <div className='tr_monogram' aria-hidden='true'>CG</div>
          <div>
            <p className='tr_eyebrow tr_eyebrow_dark'>Your instructor</p>
            <h2 className='tr_h2'>Carmen G. Garcon, MSN</h2>
            <p className='tr_body'>
              Being a nurse for decades, she devoted herself to the wellbeing of others, and caring
              has always been the focus of her career. She is now dedicating herself to preparing the
              next generation of caregivers to be professional, competent, and compassionate.
            </p>
          </div>
        </div>
      </section>

      {/* Enroll */}
      <section className='tr_section tr_navy' id='enroll'>
        <div className='tr_inner tr_enroll'>
          <div className='tr_enroll_copy'>
            <h2 className='tr_h2'>Transformative Nurse Coaching coming soon.</h2>
            <p className='tr_body'>
              Leave your information and we will follow up with you about upcoming classes,
              schedules and enrollment.
            </p>
            <p className='tr_body tr_small'>
              Prefer email? <a href='mailto:rogart409@gmail.com'>rogart409@gmail.com</a>
            </p>
          </div>
          <EnrollForm />
        </div>
      </section>

      <div className='tr_store_divider'>
        <span>MilAve Devotion Store</span>
      </div>
    </div>
  );
}

export default Training;
